import MuxPlayer from "@mux/mux-player-react";
import MuxUploader from "@mux/mux-uploader-react";
import { useCallback, useEffect, useId, useState } from "react";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";

import { CrossIcon } from "@/components/Icons";
import { Spinner } from "@/components/Spinner";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { Label } from "@/components/ui/label";
import { FragmentType, getFragmentData, graphql } from "@/gen";
import { useInterval } from "@/hooks/useInterval";
import { useViewerContext } from "@/hooks/useViewerContext";
import { cn, cx } from "@/utils/cx";

export type VideoUploadState = {
    videoId: string | null;
    progress: number;
};

const useVideo = (videoId?: string | null) => {
    const [result, refetch] = useQuery({
        query: graphql(`
            query VideoInputVideo($id: UUID!) {
                video(id: $id) {
                    id
                    thumbnailImageId
                    muxStatus
                    ...VideoPreview_Video
                }
            }
        `),
        variables: { id: videoId! },
        pause: !videoId,
    });

    useInterval(
        () => {
            if (videoId && result.data?.video?.muxStatus !== "READY") {
                refetch();
            }
        },
        videoId && result.data?.video?.muxStatus !== "READY" ? 5000 : null
    );

    return { video: result.data?.video, loading: result.fetching, error: result.error };
};

const useUploadThumbnail = (
    videoId?: string | null,
    thumbnailImageId?: string | null,
    videoThumbnailImageId?: string | null
) => {
    const [_setVideoThumbnailResult, setVideoThumbnail] = useMutation(
        graphql(`
            mutation VideoInputSetVideoThumbnail($input: SetVideoThumbnailInput!) {
                setVideoThumbnail(input: $input) {
                    id
                }
            }
        `)
    );

    useEffect(() => {
        if (videoId && thumbnailImageId && videoThumbnailImageId !== thumbnailImageId) {
            setVideoThumbnail({
                input: {
                    videoId,
                    thumbnailImageId,
                },
            });
        }
    }, [videoId, thumbnailImageId, videoThumbnailImageId, setVideoThumbnail]);
};

export const VideoInput = ({
    value,
    setValue,
    label,
    className,
}: {
    value?: VideoUploadState;
    setValue: (args: VideoUploadState) => void;
    label?: string;
    className?: string;
}) => {
    const id = useId();

    const [thumbnailImageId, setThumbnailImageId] = useState<string | null>(null);

    const { video, error } = useVideo(value?.videoId);

    useEffect(() => {
        if (video) {
            setThumbnailImageId(video.thumbnailImageId);
        }
    }, [video]);

    useEffect(() => {
        if (error) {
            toast.error("Failed to load video: " + error.message);
        }
    }, [error]);

    const [_, createMuxUploadUrl] = useMutation(
        graphql(`
            mutation VideoInputCreateMuxUploadUrl {
                createMuxUploadUrl {
                    videoId
                    uploadUrl
                }
            }
        `)
    );

    useUploadThumbnail(value?.videoId, thumbnailImageId, video?.thumbnailImageId);

    const getUploadUrl = useCallback(async () => {
        const res = await createMuxUploadUrl({});
        if (res.error || !res.data) {
            toast.error(
                `Failed to create upload URL: ${res.error?.graphQLErrors.toString() || "Unknown error"}`
            );
            return { uploadUrl: "", videoId: null };
        }

        const { uploadUrl, videoId } = res.data.createMuxUploadUrl;
        return { uploadUrl, videoId };
    }, [createMuxUploadUrl]);

    return (
        <div className={cx("flex flex-col gap-2", className)}>
            {label ? <Label htmlFor={id}>{label}</Label> : null}
            <div className={cn("flex flex-col space-y-4 border p-4")}>
                <button onClick={() => setValue({ videoId: null, progress: 0 })}>
                    <CrossIcon />
                </button>
                <MuxUploader
                    id={id}
                    noDrop
                    endpoint={async () => {
                        const { uploadUrl, videoId } = await getUploadUrl();
                        if (!uploadUrl || !videoId) return "";

                        setValue({ videoId, progress: value?.progress ?? 0 });
                        return uploadUrl;
                    }}
                    onProgress={p => {
                        setValue({
                            videoId: value?.videoId ?? null,
                            progress: (p as CustomEvent).detail,
                        });
                    }}
                    onSuccess={_ => {
                        toast.success("Video uploaded successfully. Remember to save the form!");
                        setValue({ videoId: value?.videoId ?? null, progress: 100 });
                    }}
                    onError={e => {
                        toast.error("Failed to upload video: " + e);
                    }}
                />
                {value?.videoId ? (
                    <div className="flex flex-col gap-2">
                        <Label>Upload preview thumbnail</Label>
                        <ImageUploadInput
                            imageId={thumbnailImageId}
                            onImageId={setThumbnailImageId}
                        />
                    </div>
                ) : null}
                {value?.videoId ? <VideoPreview videoFragment={video} /> : null}
            </div>
        </div>
    );
};

const VideoPreview_Video = graphql(`
    fragment VideoPreview_Video on Video {
        id
        muxStatus
        muxPlaybackId
    }
`);

const VideoPreview = ({
    videoFragment,
}: {
    videoFragment?: FragmentType<typeof VideoPreview_Video> | null;
}) => {
    const video = getFragmentData(VideoPreview_Video, videoFragment);
    const { viewer } = useViewerContext();

    if (!video) {
        return null;
    }

    if (video.muxStatus !== "READY" || !video.muxPlaybackId) {
        return (
            <div className="w-3/4 flex-row">
                Video is processing...
                <Spinner />
            </div>
        );
    }

    return (
        <MuxPlayer
            playbackId={video.muxPlaybackId}
            metadata={{
                video_id: video.id,
                video_title: "Uploaded video",
                viewer_user_id: viewer?.id,
            }}
        />
    );
};
